import { useGoogleMapsGetBrowserConfig } from 'api/google-maps/google-maps';
import Button from 'components/inputs/Button';
import Input from 'components/inputs/Input';
import { buildMapsUrl } from 'components/map/loadMapApi';
import { FC, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

// Dold felsökningsvy (bara SuperAdmin, ingen länk i menyn) för att se om en Google Maps-nyckel
// fungerar från just den här domänen. Maps-skriptet går bara att ladda en gång per sida, så varje
// test får en egen iframe med public/maps-key-test.html. Det måste vara en riktig fil på domänen:
// Google prövar webbplatsbegränsningarna mot adressen på sidan som laddar skriptet, och i en
// srcdoc-iframe är den about:srcdoc. Ramen säger till när den är laddad och får då skriptadressen
// med nyckeln via postMessage, så att nyckeln inte hamnar i någon adress eller serverlogg.
//
// Kartan ritas innan Google har prövat nyckeln: ett nej (InvalidKeyMapError, gm_authFailure)
// kommer runt tio sekunder senare. Därför sägs bara "inget fel hittills" när det varit tyst en
// stund efter att kartan skapats, och testet lyssnar vidare: ett senare nej slår om statusen.
// Har kartan inte laddats alls inom LOAD_TIMEOUT_SECONDS räknas testet som misslyckat.

const MESSAGE_SOURCE = 'maps-key-test';
const TEST_FRAME_PATH = `${process.env.PUBLIC_URL}/maps-key-test.html`;
const SETTLE_SECONDS = 20;
const LOAD_TIMEOUT_SECONDS = 30;

type TestStatus = 'loading' | 'loaded' | 'ok' | 'failed';

interface TestRun {
  id: number;
  apiKey: string;
  status: TestStatus;
  log: string[];
}

interface TestMessage {
  source: typeof MESSAGE_SOURCE;
  id: number;
  type:
    | 'ready'
    | 'loaded'
    | 'settled'
    | 'authFailure'
    | 'scriptError'
    | 'error'
    | 'warn';
  text: string;
}

const maskKey = (key: string) =>
  key.length <= 12 ? key : `${key.slice(0, 8)}…${key.slice(-4)}`;

const describeKey = (key: string) => {
  const notes: string[] = [`${key.length} tecken`];
  if (key !== key.trim())
    notes.push('har blanksteg/radbrytning i början eller slutet');
  if (key && !key.startsWith('AIza')) notes.push('börjar inte med "AIza"');
  return notes.join(', ');
};

const Page = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Title = styled.h1`
  margin: 0;
  font-size: 20px;
`;

const Explainer = styled.p`
  margin: 0;
  max-width: 780px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const InfoGrid = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 4px 16px;
  margin: 0;

  dt {
    font-weight: 600;
  }
  dd {
    margin: 0;
    font-family: monospace;
    word-break: break-all;
  }
`;

const Row = styled.form`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
`;

const Results = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(420px, 1fr));
  gap: 16px;
`;

const Card = styled.section`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border: 1px solid ${({ theme }) => theme.colors.border.primary};
  border-radius: 4px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  font-family: monospace;
`;

const Status = styled.span<{ status: TestStatus }>`
  font-family: inherit;
  font-weight: 600;
  color: ${({ theme, status }) => {
    if (status === 'ok') return theme.colors.foreground.good;
    if (status === 'failed') return theme.colors.foreground.error;
    return theme.colors.foreground.secondary;
  }};
`;

const MapFrame = styled.iframe`
  width: 100%;
  height: 260px;
  border: 0;
  background: ${({ theme }) => theme.colors.background.secondary};
`;

const Log = styled.pre`
  margin: 0;
  max-height: 160px;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-all;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.foreground.secondary};
`;

const statusLabel: Record<TestStatus, string> = {
  loading: 'Laddar…',
  loaded: 'Kartan ritad, väntar på Google…',
  ok: `Inget fel på ${SETTLE_SECONDS} s`,
  failed: 'Fungerar inte',
};

let nextTestId = 1;

const TestMapsApiKey: FC = () => {
  const { data: browserConfig, isLoading } = useGoogleMapsGetBrowserConfig();
  const configuredKey = browserConfig?.browserApiKey ?? '';
  const [keyInput, setKeyInput] = useState('');
  const [runs, setRuns] = useState<TestRun[]>([]);
  // Nyckeln för ett test som väntar på att dess ram ska säga till. Tas bort när ramen fått den, så
  // att en omladdad ram inte laddar kartskriptet en gång till.
  const pendingKeysByRunId = useRef(new Map<number, string>());

  useEffect(() => {
    const onMessage = (event: MessageEvent<TestMessage>) => {
      const msg = event.data;
      if (event.origin !== window.location.origin) return;
      if (msg?.source !== MESSAGE_SOURCE) return;

      if (msg.type === 'ready') {
        const apiKey = pendingKeysByRunId.current.get(msg.id);
        if (apiKey === undefined || !event.source) return;
        pendingKeysByRunId.current.delete(msg.id);
        (event.source as Window).postMessage(
          {
            source: MESSAGE_SOURCE,
            id: msg.id,
            type: 'start',
            scriptUrl: buildMapsUrl(apiKey),
            settleSeconds: SETTLE_SECONDS,
          },
          window.location.origin
        );
      }

      setRuns((prev) =>
        prev.map((run) => {
          if (run.id !== msg.id) return run;
          let { status } = run;
          const log = [...run.log, `[${msg.type}] ${msg.text}`];
          const rejected =
            msg.type === 'authFailure' ||
            msg.type === 'scriptError' ||
            (msg.type === 'error' && msg.text.includes('Maps'));
          if (rejected) {
            if (status === 'ok') {
              log.push(
                `Google nekade nyckeln efter mer än ${SETTLE_SECONDS} s, trots att det först såg bra ut`
              );
            }
            status = 'failed';
          } else if (msg.type === 'loaded' && status === 'loading') {
            status = 'loaded';
          } else if (msg.type === 'settled' && status === 'loaded') {
            status = 'ok';
          }
          return { ...run, status, log };
        })
      );
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  const startTest = (apiKey: string) => {
    const id = nextTestId;
    nextTestId += 1;
    pendingKeysByRunId.current.set(id, apiKey);
    setRuns((prev) => [{ id, apiKey, status: 'loading', log: [] }, ...prev]);

    // Kartskriptet laddades aldrig (blockerat, fel i skriptet, ingen kontakt med Google).
    window.setTimeout(() => {
      setRuns((prev) =>
        prev.map((run) =>
          run.id === id && run.status === 'loading'
            ? {
                ...run,
                status: 'failed',
                log: [
                  ...run.log,
                  `Kartan laddades inte inom ${LOAD_TIMEOUT_SECONDS} s`,
                ],
              }
            : run
        )
      );
    }, LOAD_TIMEOUT_SECONDS * 1000);
  };

  return (
    <Page>
      <Title>Testa Google Maps-nyckel</Title>
      <Explainer>
        Varje test laddar Google Maps i en egen ram med nyckeln du anger, från
        den här domänen. Google kollar nyckelns webbplatsbegränsningar mot
        sidans adress, så ett test här visar om nyckeln fungerar för just den
        här miljön. Felkoder som RefererNotAllowedMapError,
        ApiNotActivatedMapError eller BillingNotEnabledMapError syns i loggen
        under kartan.
      </Explainer>

      <InfoGrid>
        <dt>Sidans adress (referer)</dt>
        <dd>{window.location.origin}</dd>
        <dt>Konfigurerad nyckel</dt>
        <dd>
          {isLoading && 'Hämtar…'}
          {!isLoading &&
            (configuredKey
              ? `${maskKey(configuredKey)} (${describeKey(configuredKey)})`
              : 'Tom — GoogleMaps:BrowserApiKey är inte satt')}
        </dd>
      </InfoGrid>

      <Row
        onSubmit={(e) => {
          e.preventDefault();
          if (keyInput) startTest(keyInput);
        }}
      >
        <Input
          width={420}
          value={keyInput}
          placeholder="Klistra in en API-nyckel"
          onChange={(e) => setKeyInput(e.target.value)}
        />
        <Button type="submit" disabled={!keyInput}>
          Testa nyckeln
        </Button>
        <Button
          type="button"
          disabled={!configuredKey}
          onClick={() => startTest(configuredKey)}
        >
          Testa konfigurerad nyckel
        </Button>
      </Row>

      <Results>
        {runs.map((run) => (
          <Card key={run.id}>
            <CardHeader>
              <span>
                #{run.id} {maskKey(run.apiKey)}
              </span>
              <Status status={run.status}>{statusLabel[run.status]}</Status>
            </CardHeader>
            <Explainer>{describeKey(run.apiKey)}</Explainer>
            <MapFrame
              title={`Karttest ${run.id}`}
              src={`${TEST_FRAME_PATH}#id=${run.id}`}
            />
            <Log>{run.log.length ? run.log.join('\n') : 'Väntar på svar…'}</Log>
          </Card>
        ))}
      </Results>
    </Page>
  );
};

export default TestMapsApiKey;
